import React from 'react';
import { TextField, Dropdown, Section, NumberField } from 'm2m.components';
import { multaMoraLabels } from 'common/genericDescriptions';

interface MultaMoraFieldsProps {
  percentualMulta?: number | null;
  periodicidadeMulta?: number | null;
  percentualMora?: number | null;
  periodicidadeMora?: number | null;
  onUpdateField: (field: any) => void;
  showError: (fieldName: string) => boolean;
  labels?: {
    percentualMulta: string;
    periodicidadeMulta: string;
    percentualMora: string;
    periodicidadeMora: string;
  };
  useTextFields?: boolean; // Para componentes que usam TextField ao invés de NumberField
}

const MultaMoraFields: React.FC<MultaMoraFieldsProps> = ({
  percentualMulta,
  periodicidadeMulta,
  percentualMora,
  periodicidadeMora,
  onUpdateField,
  showError,
  labels = multaMoraLabels,
  useTextFields = false
}) => {
  const periodicidadeMultaOptions = [
    { value: '', description: 'Selecione...' },
    { value: '0', description: 'Diário' },
    { value: '1', description: 'Mensal' },
    { value: '2', description: 'Anual' }
  ];

  const periodicidadeMoraOptions = [
    { value: '', description: 'Selecione...' },
    { value: '0', description: 'Diário' },
    { value: '1', description: 'Mensal' },
    { value: '2', description: 'Anual' }
  ];

  const applyDecimalMask = (value: string, maxValue: number): string => {
    // Remove caracteres inválidos, mantém apenas números, vírgula e ponto
    let masked = value.replace(/[^0-9.,]/g, '');
    
    // Converte vírgula para ponto
    masked = masked.replace(',', '.');
    
    // Garante apenas um ponto decimal
    const parts = masked.split('.');
    if (parts.length > 2) {
      masked = parts[0] + '.' + parts[1];
    }
    
    // Limita a 1 dígito antes do ponto para multa (max 1.0) ou 1 dígito para mora (max 2.0)
    if (parts[0] && parts[0].length > 1) {
      if (maxValue === 1 && parseInt(parts[0]) > 1) {
        masked = '1' + (parts[1] ? '.' + parts[1] : '');
      } else if (maxValue === 2 && parseInt(parts[0]) > 2) {
        masked = '2' + (parts[1] ? '.' + parts[1] : '');
      }
    }
    
    // Limita a 1 casa decimal
    if (parts.length === 2 && parts[1].length > 1) {
      masked = parts[0] + '.' + parts[1].substring(0, 1);
    }
    
    // Valida valor máximo final
    const numValue = parseFloat(masked);
    if (!isNaN(numValue) && numValue > maxValue) {
      return maxValue.toString();
    }
    
    return masked;
  };

  const renderPercentualField = (
    fieldName: 'percentualMulta' | 'percentualMora',
    value: number | null | undefined,
    maxValue: number,
    label: string
  ) => {
    const [displayValue, setDisplayValue] = React.useState(value?.toString() || '');

    // Sync display value when prop value changes
    React.useEffect(() => {
      setDisplayValue(value?.toString() || '');
    }, [value]);

    return (
      <TextField
        label={`${label} (máx ${maxValue}%)`}
        value={displayValue}
        placeholder={`Ex: 0.5, ${maxValue === 1 ? '0.8' : '1.5'}`}
        onChange={(inputValue) => {
          // Apply mask in real time
          const maskedValue = applyDecimalMask(inputValue, maxValue);
          setDisplayValue(maskedValue);
          
          // Update store only with valid numbers
          if (maskedValue === '') {
            onUpdateField({ name: fieldName, value: undefined });
          } else {
            const numericValue = parseFloat(maskedValue);
            if (!isNaN(numericValue)) {
              onUpdateField({ name: fieldName, value: numericValue });
            }
          }
        }}
        inputProps={{ 
          inputMode: 'decimal',
          pattern: '[0-9]*[.,]?[0-9]*',
          step: '0.01',
          min: '0',
          max: maxValue.toString()
        }}
        error={showError(label)}
      />
    );
  };

  return (
    <Section title="Atraso do Pagamento e encargos" defaultGrid>
      {renderPercentualField('percentualMulta', percentualMulta, 1, labels.percentualMulta)}
      
      <Dropdown
        label={labels.periodicidadeMulta}
        options={periodicidadeMultaOptions}
        value={periodicidadeMulta?.toString() || ''}
        onChange={(value) =>
          onUpdateField({
            name: 'periodicidadeMulta',
            value: value === '' ? undefined : parseInt(value),
          })
        }
        error={showError(labels.periodicidadeMulta)}
      />

      {renderPercentualField('percentualMora', percentualMora, 2, labels.percentualMora)}
      
      <Dropdown
        label={labels.periodicidadeMora}
        options={periodicidadeMoraOptions}
        value={periodicidadeMora?.toString() || ''}
        onChange={(value) =>
          onUpdateField({
            name: 'periodicidadeMora',
            value: value === '' ? undefined : parseInt(value),
          })
        }
        error={showError(labels.periodicidadeMora)}
      />
    </Section>
  );
};

export default MultaMoraFields;
